import {
  ManualAdjustmentKey,
  ORCHESTRATE_AUTOFILL_PROPERTY_API,
  OrderFeature,
  SourceType,
  UnitRateKey,
  schManualAdjustmentsKey,
  schSourceType,
  schUnitRateKey,
} from '@aloft/toolkit-shared';
import { Opts } from '@aloft/toolkit-sui';

import { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { z } from 'zod';

export type PropertyGridType = 'FeatureGrid' | 'AdjustmentsGrid' | 'SensitivityGrid';

const schColumnType = schSourceType.or(z.literal('property'));
export type PropertyGridColumnType = z.infer<typeof schColumnType>;

type PropertyGridColumnDefinitionShape = {
  propertyId: string;
  type: PropertyGridColumnType;
  propertyData?: z.infer<typeof ORCHESTRATE_AUTOFILL_PROPERTY_API.GET.RESPONSE>;
};

export const schPropertyGridColumnDefinition: z.ZodType<PropertyGridColumnDefinitionShape> =
  z.object({
    propertyId: z.string(),
    type: schColumnType,
    propertyData: ORCHESTRATE_AUTOFILL_PROPERTY_API.GET.RESPONSE.optional(),
  });
export type PropertyGridColumnDefinition = z.infer<typeof schPropertyGridColumnDefinition>;

type PropertyGridFormShape = {
  columns: PropertyGridColumnDefinition[];
  dataSources: SourceType[];
};

export const schPropertyGridForm: z.ZodType<PropertyGridFormShape> = z.object({
  columns: z.array(schPropertyGridColumnDefinition),
  dataSources: z.array(schSourceType),
});
export type PropertyGridForm = PropertyGridFormShape;

type ImprovementsFieldName =
  | 'editableData.improvements'
  | 'editableData.improvements.propertyType'
  | 'editableData.improvements.age'
  | 'editableData.improvements.bedrooms'
  | 'editableData.improvements.fullBaths'
  | 'editableData.improvements.halfBaths'
  | 'editableData.improvements.gla'
  | 'editableData.improvements.uadQuality'
  | 'editableData.improvements.uadCondition'
  | 'editableData.improvements.featureGridDataSources'
  | 'editableData.improvements.manualCostDisplayName'
  | 'editableData.improvements.manualCost'
  | 'editableData.improvements.adu.area'
  | 'editableData.improvements.adu.quality'
  | 'editableData.improvements.adu.fullBaths'
  | 'editableData.improvements.adu.halfBaths'
  | 'editableData.improvements.attic.area'
  | 'editableData.improvements.attic.quality'
  | 'editableData.improvements.barn.area'
  | 'editableData.improvements.barn.quality'
  | 'editableData.improvements.basement.totalArea'
  | 'editableData.improvements.basement.finishedArea'
  | 'editableData.improvements.basement.fullBaths'
  | 'editableData.improvements.basement.halfBaths'
  | 'editableData.improvements.carport.area'
  | 'editableData.improvements.carport.spaces'
  | 'editableData.improvements.carport.quality'
  | 'editableData.improvements.deck.type'
  | 'editableData.improvements.deck.area'
  | 'editableData.improvements.deck.quality'
  | 'editableData.improvements.detachedGarage.area'
  | 'editableData.improvements.detachedGarage.spaces'
  | 'editableData.improvements.detachedGarage.quality'
  | 'editableData.improvements.fireplace.type'
  | 'editableData.improvements.fireplace.count'
  | 'editableData.improvements.garage.area'
  | 'editableData.improvements.garage.spaces'
  | 'editableData.improvements.garage.quality'
  | 'editableData.improvements.patio.type'
  | 'editableData.improvements.patio.area'
  | 'editableData.improvements.patio.quality'
  | 'editableData.improvements.pool.area'
  | 'editableData.improvements.pool.quality'
  | 'editableData.improvements.porch.type'
  | 'editableData.improvements.porch.area'
  | 'editableData.improvements.porch.quality'
  | 'editableData.improvements.shed.area'
  | 'editableData.improvements.shed.quality'
  | 'editableData.improvements.spa.area'
  | 'editableData.improvements.spa.quality'
  | 'editableData.improvements.workshop.area'
  | 'editableData.improvements.workshop.quality';

type ArvImprovementsFieldName =
  `editableData.arv.${ImprovementsFieldName extends `editableData.${infer Rest}` ? Rest : never}`;

const IMPROVEMENTS_FIELD_NAMES = [
  'editableData.improvements',
  'editableData.improvements.propertyType',
  'editableData.improvements.age',
  'editableData.improvements.bedrooms',
  'editableData.improvements.fullBaths',
  'editableData.improvements.halfBaths',
  'editableData.improvements.gla',
  'editableData.improvements.uadQuality',
  'editableData.improvements.uadCondition',
  'editableData.improvements.featureGridDataSources',
  'editableData.improvements.manualCostDisplayName',
  'editableData.improvements.manualCost',
  'editableData.improvements.adu.area',
  'editableData.improvements.adu.quality',
  'editableData.improvements.adu.fullBaths',
  'editableData.improvements.adu.halfBaths',
  'editableData.improvements.attic.area',
  'editableData.improvements.attic.quality',
  'editableData.improvements.barn.area',
  'editableData.improvements.barn.quality',
  'editableData.improvements.basement.totalArea',
  'editableData.improvements.basement.finishedArea',
  'editableData.improvements.basement.fullBaths',
  'editableData.improvements.basement.halfBaths',
  'editableData.improvements.carport.area',
  'editableData.improvements.carport.spaces',
  'editableData.improvements.carport.quality',
  'editableData.improvements.deck.type',
  'editableData.improvements.deck.area',
  'editableData.improvements.deck.quality',
  'editableData.improvements.detachedGarage.area',
  'editableData.improvements.detachedGarage.spaces',
  'editableData.improvements.detachedGarage.quality',
  'editableData.improvements.fireplace.type',
  'editableData.improvements.fireplace.count',
  'editableData.improvements.garage.area',
  'editableData.improvements.garage.spaces',
  'editableData.improvements.garage.quality',
  'editableData.improvements.patio.type',
  'editableData.improvements.patio.area',
  'editableData.improvements.patio.quality',
  'editableData.improvements.pool.area',
  'editableData.improvements.pool.quality',
  'editableData.improvements.porch.type',
  'editableData.improvements.porch.area',
  'editableData.improvements.porch.quality',
  'editableData.improvements.shed.area',
  'editableData.improvements.shed.quality',
  'editableData.improvements.spa.area',
  'editableData.improvements.spa.quality',
  'editableData.improvements.workshop.area',
  'editableData.improvements.workshop.quality',
] as const satisfies readonly ImprovementsFieldName[];

const ARV_IMPROVEMENTS_FIELD_NAMES = IMPROVEMENTS_FIELD_NAMES.map(
  (f) => f.replace('editableData.', 'editableData.arv.') as ArvImprovementsFieldName,
);

export const schPropertyGridFieldName = z.enum([
  'editableData.transaction',
  'editableData.transaction.status',
  'editableData.transaction.price',
  'editableData.transaction.originalListPrice',
  'editableData.transaction.contractDate',
  'editableData.transaction.saleDate',
  'editableData.transaction.daysOnMarket',
  'editableData.transaction.rentRate',
  'editableData.transaction.leaseDate',
  'editableData.transaction.expirationDate',
  'editableData.transaction.withdrawalDate',
  'editableData.site',
  'editableData.site.area',
  'editableData.site.influence',
  'editableData.site.influenceRating',
  'editableData.site.view',
  'editableData.site.viewRating',
  ...IMPROVEMENTS_FIELD_NAMES,
  ...ARV_IMPROVEMENTS_FIELD_NAMES,
]);

export type PropertyGridFieldName = z.infer<typeof schPropertyGridFieldName>;

const schPropertyGridCellKeyId = z.union([
  schPropertyGridFieldName,
  schUnitRateKey,
  schManualAdjustmentsKey,
  z.literal('totalCostToCure'),
  z.literal('netAdjustmentPct'),
  z.literal('grossAdjustmentPct'),
  z.literal('adjustedSalePrice'),
]);

export type PropertyGridCellKeyId = z.infer<typeof schPropertyGridCellKeyId>;

export type PropertyGridCellKey =
  | `subject-${PropertyGridCellKeyId}`
  | `${string}-${PropertyGridCellKeyId}`;

const tryParsePropertyGridCellKey = (val: string): PropertyGridCellKey | undefined => {
  if (!val.includes('-')) {
    return undefined;
  }

  let dashIndex = val.indexOf('-');
  while (dashIndex !== -1) {
    const cellKeyId = val.slice(dashIndex + 1);
    if (schPropertyGridCellKeyId.safeParse(cellKeyId).success) {
      return val as PropertyGridCellKey;
    }
    dashIndex = val.indexOf('-', dashIndex + 1);
  }

  return undefined;
};

export const parsePropertyGridCellKey = (val: string): PropertyGridCellKey | undefined =>
  tryParsePropertyGridCellKey(val);

export const getPropertyIdFromPropertyGridCellKey = (cellKey: PropertyGridCellKey): string => {
  if (cellKey.startsWith('subject-')) {
    return 'subject';
  }

  let dashIndex = cellKey.indexOf('-');
  while (dashIndex !== -1) {
    const cellKeyId = cellKey.slice(dashIndex + 1);
    if (schPropertyGridCellKeyId.safeParse(cellKeyId).success) {
      return cellKey.slice(0, dashIndex);
    }
    dashIndex = cellKey.indexOf('-', dashIndex + 1);
  }

  return cellKey.slice(0, cellKey.indexOf('-'));
};

export const schPropertyGridCellKey = z.custom<PropertyGridCellKey>(
  (val) => typeof val === 'string' && tryParsePropertyGridCellKey(val) !== undefined,
);

export type PropertyGridRowFieldType =
  | 'dataSourceValue'
  | 'label'
  | 'editableValue'
  | 'editableArvValue'
  | 'readOnlyArvValue'
  | 'readOnlyValue'
  | 'readOnlyUnitRate'
  | 'readOnlyDifference'
  | 'adjustment'
  | 'editableUnitRate';

export type PropertyGridSectionProps = {
  columnIndex: number;
  fieldTypes: PropertyGridRowFieldType[];
  gridType: PropertyGridType;
  feature?: OrderFeature;
  isRemovable?: boolean;
  hasSeparateArvValue?: boolean;
  hasUnitRate?: boolean;
  onRemove?: () => void;
  control: Control<PropertyGridForm>;
  getValues: UseFormGetValues<PropertyGridForm>;
  setValue: UseFormSetValue<PropertyGridForm>;
};

export type PropertyGridColumnSectionComponent = React.ComponentType<PropertyGridSectionProps>;

export type PropertyGridField<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T = any,
  U extends string | number = T extends string | number ? T : string,
> = {
  fieldName: PropertyGridFieldName;
  label: string;
  manualAdjustmentKey?: ManualAdjustmentKey;
  /** Unit label shown after the value (e.g. "sf", "bd"). Displayed with a "/" prefix in unit rate fields. */
  unit?: string;
  unitRateKey?: UnitRateKey;
  options?: Opts<U>; // For enum fields
  includeEmptyOption?: boolean;
  autofillSubjectValue?: boolean;
  tooltipContent?: string;
  required?: boolean;
  allowExplicitUnknown?: boolean;
  toDisplayValue?: (
    value: T | undefined,
    propertyId: string,
    options?: { isArv?: boolean },
  ) => string | undefined;
  toEditValue?: (value: T | undefined, propertyId: string) => string | undefined;
  toValue?: (value: string | undefined, propertyId: string) => T | undefined;
  /** Extracts a raw numeric value from the stored field value. Used by ReadOnlyDifferenceField to compute the numeric difference between the subject and a comp. */
  toNumericValue?: (value: T | undefined) => number | undefined;
  /**
   * When provided, overrides the label shown in the trigger button of an EditableEnumField
   * (the closed state). Useful when the composite display string differs from the enum value
   * used for option matching (e.g. "Q3/4.00" trigger label while the value is just 3).
   */
  toTriggerLabel?: (value: T | undefined, propertyId: string) => string | undefined;
};
